import { useState } from 'react'
import {
  Card, Row, Col, Form, DatePicker, Select, Button, Table, Tabs,
  Statistic, Tag, Space, Typography,
} from 'antd'
import {
  DownloadOutlined, SearchOutlined, CarOutlined,
  WarningOutlined, CheckCircleOutlined, StopOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { reportsApi } from '@/features/reports/api'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const { RangePicker } = DatePicker

export default function GateReportsPage() {
  const { plantId, setPlantId, plants } = usePlantId()
  const [form] = Form.useForm()
  const [params, setParams] = useState({
    plant_id: '',
    date_from: dayjs().startOf('month').format('YYYY-MM-DD'),
    date_to: dayjs().format('YYYY-MM-DD'),
  })

  // Sync plantId from hook into params
  const activeParams = { ...params, plant_id: plantId }

  const { data: gateSummary, isLoading: gateLoading } = useQuery({
    queryKey: ['gate-summary', activeParams],
    queryFn: () => reportsApi.gateSummary(activeParams),
    enabled: !!plantId,
  })

  const { data: reconciliation, isLoading: reconcLoading } = useQuery({
    queryKey: ['weighbridge-reconciliation', activeParams],
    queryFn: () => reportsApi.weighbridgeReconciliation(activeParams),
    enabled: !!plantId,
  })

  const { data: shortage } = useQuery({
    queryKey: ['shortage-analysis', activeParams],
    queryFn: () => reportsApi.shortageAnalysis(activeParams),
    enabled: !!plantId,
  })

  const { data: visitorSummary } = useQuery({
    queryKey: ['visitor-summary', activeParams],
    queryFn: () => reportsApi.visitorSummary(activeParams),
    enabled: !!plantId,
  })

  const handleSearch = () => {
    const vals = form.getFieldsValue()
    const [df, dt] = vals.dateRange || []
    if (vals.plant_id) setPlantId(vals.plant_id)
    setParams({
      plant_id: vals.plant_id || plantId,
      date_from: df ? df.format('YYYY-MM-DD') : params.date_from,
      date_to: dt ? dt.format('YYYY-MM-DD') : params.date_to,
    })
  }

  const reconcColumns = [
    { title: 'Inward No', dataIndex: 'inward_no', key: 'inward_no', width: 120 },
    { title: 'Date', dataIndex: 'entry_date', key: 'entry_date', width: 120 },
    { title: 'Supplier', dataIndex: 'supplier', key: 'supplier', width: 160, ellipsis: true },
    { title: 'Invoice Wt (kg)', dataIndex: 'invoice_weight_kg', key: 'invoice_wt', align: 'right' as const },
    { title: 'Net Wt (kg)', dataIndex: 'net_weight_kg', key: 'net_wt', align: 'right' as const },
    { title: 'Shortage (kg)', dataIndex: 'shortage_kg', key: 'shortage', align: 'right' as const },
    {
      title: 'Shortage %', dataIndex: 'shortage_pct', key: 'shortage_pct', align: 'right' as const,
      render: (v: string) => {
        const n = parseFloat(v)
        return <span style={{ color: n > 1 ? '#cf1322' : n > 0.5 ? '#d46b08' : '#389e0d' }}>{v}%</span>
      }
    },
    {
      title: 'Action', dataIndex: 'shortage_action', key: 'action',
      render: (v: string) => {
        const colors: Record<string, string> = {
          OK: 'green', DEBIT_NOTE: 'gold', MD_ALERT: 'orange', HARD_BLOCK: 'red',
        }
        return <Tag color={colors[v] || 'default'}>{v}</Tag>
      }
    },
    { title: 'Comm. Wt (kg)', dataIndex: 'commercial_weight_kg', key: 'comm_wt', align: 'right' as const },
    { title: 'Debit Note', dataIndex: 'debit_note_amt', key: 'debit', align: 'right' as const },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <CarOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Gate & Weighbridge Reports
      </Title>
      <Typography.Text type="secondary">Gate entries, weight reconciliation, visitor log</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="plant_id" label="Plant">
            <Select
              style={{ width: 180 }}
              value={plantId}
              onChange={v => setPlantId(v)}
              options={plants.map((p: { id: string; name: string }) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
          <Form.Item name="dateRange" label="Period">
            <RangePicker defaultValue={[dayjs().startOf('month'), dayjs()]} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>Apply</Button>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button icon={<DownloadOutlined />} href={reportsApi.gateEntriesExportUrl(activeParams)} download>
                Gate Entries
              </Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.weighbridgeExportUrl(activeParams)} download>
                Weighbridge
              </Button>
              <Button icon={<DownloadOutlined />} href={reportsApi.visitorExportUrl(activeParams)} download>
                Visitors
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Tabs
        defaultActiveKey="gate"
        style={{ marginTop: 16 }}
        items={[
          {
            key: 'gate',
            label: 'Gate Summary',
            children: (
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Card><Statistic title="Total Entries" value={gateSummary?.total_entries ?? 0} loading={gateLoading} /></Card>
                </Col>
                <Col span={6}>
                  <Card><Statistic title="Invoice Wt (kg)" value={gateSummary?.total_invoice_weight_kg ?? 0} precision={3} loading={gateLoading} /></Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic title="Shortage Lots" value={shortage ? (shortage.debit_note + shortage.md_alert + shortage.hard_block) : 0}
                      valueStyle={{ color: '#cf1322' }} prefix={<WarningOutlined />} />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card><Statistic title="Avg Shortage %" value={shortage?.avg_shortage_pct ?? 0} precision={4} suffix="%" /></Card>
                </Col>
                {shortage && (
                  <Col span={24}>
                    <Card title="Shortage Action Summary">
                      <Row gutter={16}>
                        <Col span={4}><Statistic title="OK" value={shortage.ok} valueStyle={{ color: '#389e0d' }} prefix={<CheckCircleOutlined />} /></Col>
                        <Col span={4}><Statistic title="Debit Note" value={shortage.debit_note} valueStyle={{ color: '#d46b08' }} /></Col>
                        <Col span={4}><Statistic title="MD Alert" value={shortage.md_alert} valueStyle={{ color: '#d46b08' }} prefix={<WarningOutlined />} /></Col>
                        <Col span={4}><Statistic title="Hard Block" value={shortage.hard_block} valueStyle={{ color: '#cf1322' }} prefix={<StopOutlined />} /></Col>
                        <Col span={4}><Statistic title="Total Shortage (kg)" value={shortage.total_shortage_kg} precision={3} /></Col>
                      </Row>
                    </Card>
                  </Col>
                )}
              </Row>
            ),
          },
          {
            key: 'weighbridge',
            label: 'Weighbridge Reconciliation',
            children: (
              <Card extra={<Button size="small" icon={<DownloadOutlined />} href={reportsApi.weighbridgeExportUrl(activeParams)} download>Export Excel</Button>}>
                <Table dataSource={Array.isArray(reconciliation) ? reconciliation : []} columns={reconcColumns}
                  rowKey="inward_no" loading={reconcLoading} scroll={{ x: 1200 }} size="small" pagination={{ pageSize: 20 }} />
              </Card>
            ),
          },
          {
            key: 'visitors',
            label: 'Visitor Summary',
            children: (
              <Row gutter={[16, 16]}>
                <Col span={6}><Card><Statistic title="Total Visitors" value={visitorSummary?.total ?? 0} /></Card></Col>
                {visitorSummary?.by_purpose && (
                  <Col span={24}>
                    <Card title="Visitors by Purpose">
                      <Row gutter={16}>
                        {Object.entries(visitorSummary.by_purpose as Record<string, number>).map(([purpose, count]) => (
                          <Col key={purpose} span={4}><Statistic title={purpose} value={count} /></Col>
                        ))}
                      </Row>
                    </Card>
                  </Col>
                )}
                <Col span={24}>
                  <Button icon={<DownloadOutlined />} href={reportsApi.visitorExportUrl(activeParams)} download type="primary">
                    Download Visitor Log Excel
                  </Button>
                </Col>
              </Row>
            ),
          },
        ]}
      />
    </div>
  )
}
